import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

/**
 * Bouton, puce et compteur.
 *
 * Une seule règle explique la forme de ce fichier : **un composant ne choisit jamais une
 * couleur**, il choisit une intention (`principal`, `danger`, `marque`), et la feuille de style
 * décide de la couleur à partir des jetons. C'est ce qui rend le changement de marque (D17) et le
 * mode sombre possibles sans toucher à un seul écran.
 */

type Apparence = 'principal' | 'secondaire' | 'discret' | 'danger' | 'marque'
type Taille = 'sm' | 'md' | 'lg'

function classes(apparence: Apparence, taille: Taille, bloc: boolean, charge: boolean, ajout?: string) {
  return [
    'bouton',
    apparence === 'principal' ? '' : `bouton--${apparence}`,
    taille === 'md' ? '' : `bouton--${taille}`,
    bloc ? 'bouton--bloc' : '',
    charge ? 'bouton--charge' : '',
    ajout ?? '',
  ]
    .filter((classe) => classe !== '')
    .join(' ')
}

interface BoutonProps {
  children: ReactNode
  apparence?: Apparence
  taille?: Taille
  bloc?: boolean
  /** Affiche le rouet ET neutralise le bouton : un bouton qui travaille ne se clique pas deux fois. */
  charge?: boolean
  desactive?: boolean
  type?: 'button' | 'submit'
  className?: string
  ariaLabel?: string
  onClick?: () => void
}

export function Bouton({
  children,
  apparence = 'principal',
  taille = 'md',
  bloc = false,
  charge = false,
  desactive = false,
  type = 'button',
  className,
  ariaLabel,
  onClick,
}: BoutonProps) {
  return (
    <button
      type={type}
      className={classes(apparence, taille, bloc, charge, className)}
      disabled={desactive || charge}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

/**
 * Le même bouton, mais qui navigue.
 *
 * Un lien reste un lien : il s'ouvre dans un nouvel onglet, se copie, s'indexe. Le déguiser en
 * bouton `onClick` casse tout cela pour un gain nul.
 *
 * **Par le routeur, jamais par un `<a href>` brut.** Un `href` interne recharge l'application
 * entière — et surtout, il perd le préfixe de langue, qui est le `basename` du routeur : un
 * visiteur arabophone se retrouvait renvoyé au français. `externe` reste pour ce qui sort
 * vraiment du site.
 */
export function LienBouton({
  children,
  to,
  externe = false,
  apparence = 'principal',
  taille = 'md',
  bloc = false,
  className,
}: {
  children: ReactNode
  to: string
  externe?: boolean
  apparence?: Apparence
  taille?: Taille
  bloc?: boolean
  className?: string
}) {
  const classe = classes(apparence, taille, bloc, false, className)

  if (externe) {
    return (
      <a href={to} className={classe}>
        {children}
      </a>
    )
  }

  return (
    <Link to={to} className={classe}>
      {children}
    </Link>
  )
}

/**
 * Puce d'information.
 *
 * Nommée `puce` et non `pastille` : `pastille` désigne déjà le compteur de non-lus dans les
 * écrans hérités. Deux sens pour un même nom, c'est exactement la configuration qui a écrasé la
 * barre de navigation pendant deux jours.
 */
export function Puce({
  children,
  ton,
}: {
  children: ReactNode
  ton?: 'accent' | 'succes' | 'attention' | 'danger' | 'info' | 'marque'
}) {
  return <span className={ton === undefined ? 'puce' : `puce puce--${ton}`}>{children}</span>
}

/** Compteur de non-lus. Zéro ne s'affiche pas : un compteur à zéro est du bruit. */
export function Compteur({ valeur, langue }: { valeur: number; langue: string }) {
  if (valeur <= 0) {
    return null
  }

  return <span className="compteur">{new Intl.NumberFormat(langue).format(valeur)}</span>
}
